import { api } from './_client';
import type {
  CreateProductInput,
  DeleteReleaseResponse,
  DownloadLinkResponse,
  PublishReleaseInput,
  ReleaseUploadResponse,
  SoftwareProductListResponse,
  SoftwareProductRow,
  SoftwareRelease,
  SoftwareReleaseListResponse,
  UpdateProductInput,
} from '../../types/downloads';

/**
 * Multipart body for the proxied installer upload.
 *
 * Extracted so the field set can be asserted without a browser: the backend
 * reads `product`/`version`/`channel` as Form fields and takes the release's
 * file name from the part's own filename, so a missing field or a filename
 * that disagrees with `input.file_name` breaks publish in a way that only
 * surfaces against a 200 MB upload. The filename is passed explicitly for
 * exactly that reason — it pins the name the backend will build the S3 key
 * from to the same string the follow-up finalize call declares.
 */
export function buildReleaseUploadForm(input: PublishReleaseInput, file: Blob): FormData {
  const form = new FormData();
  form.append('product', input.product);
  form.append('version', input.version);
  form.append('channel', input.channel);
  form.append('file', file, input.file_name);
  return form;
}

export const softwareReleasesApi = {
  async getSoftwareReleases(): Promise<SoftwareReleaseListResponse> {
    const response = await api.get<SoftwareReleaseListResponse>('/api/software-releases');
    return response.data;
  },

  async requestSoftwareReleaseDownload(releaseId: string): Promise<DownloadLinkResponse> {
    const response = await api.post<DownloadLinkResponse>(
      `/api/software-releases/${releaseId}/download`
    );
    return response.data;
  },

  /**
   * Upload the installer's bytes THROUGH the backend (step 1 of publish).
   *
   * Not a direct-to-S3 PUT: that request is cross-origin and preflights, and
   * the bucket carries no CORS rule, so it never left the browser — see
   * `docs/GOTCHAS.md` G-7. Going through the shared `api` instance also means
   * the Supabase JWT rides along on the interceptor, like every other upload
   * in the product.
   *
   * `timeout: 0` because the instance default is 10 s and an installer is
   * minutes on an office uplink. Content-Type is left unset on purpose so the
   * browser writes the multipart boundary.
   */
  async uploadSoftwareReleaseBinary(
    input: PublishReleaseInput,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ReleaseUploadResponse> {
    const response = await api.post<ReleaseUploadResponse>(
      '/api/software-releases/upload',
      buildReleaseUploadForm(input, file),
      {
        timeout: 0,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      }
    );
    return response.data;
  },

  async finalizeSoftwareRelease(input: PublishReleaseInput): Promise<SoftwareRelease> {
    // The backend verifies the uploaded object and streams it for a SHA256 —
    // for a ~200 MB installer that takes tens of seconds, so override the
    // instance-wide 10 s timeout.
    const response = await api.post<SoftwareRelease>('/api/software-releases', input, {
      timeout: 300000,
    });
    return response.data;
  },

  async deleteSoftwareRelease(releaseId: string): Promise<DeleteReleaseResponse> {
    const response = await api.delete<DeleteReleaseResponse>(`/api/software-releases/${releaseId}`);
    return response.data;
  },

  // -- Products (XER admin only) ------------------------------------------
  // The catalog's product list. Archived products are returned flagged, not
  // filtered: this is the surface they are unarchived from.

  async getSoftwareProducts(): Promise<SoftwareProductListResponse> {
    const response = await api.get<SoftwareProductListResponse>('/api/software-releases/products');
    return response.data;
  },

  /** Create a product. `audience` is fixed at creation and immutable after —
   * changing visibility means creating a new key. */
  async createSoftwareProduct(input: CreateProductInput): Promise<SoftwareProductRow> {
    const response = await api.post<SoftwareProductRow>('/api/software-releases/products', input);
    return response.data;
  },

  /** Rename, reorder, archive or unarchive. `key` and `audience` are not
   * accepted by the endpoint — sending either is a 422, deliberately. */
  async updateSoftwareProduct(key: string, input: UpdateProductInput): Promise<SoftwareProductRow> {
    const response = await api.patch<SoftwareProductRow>(
      `/api/software-releases/products/${encodeURIComponent(key)}`,
      input
    );
    return response.data;
  },
};
